import { useState, useRef, useEffect } from 'react';
import { useEditor } from '@/lib/kinema';
import { UPDATE_PROPERTY, SELECT_LAYER } from '@/lib/kinema';
import type { BaseLayerData } from '@/lib/kinema';
import type { AdTokenKind } from '@/shared/adTokens/adTokenScan';
import AdTokenInput from '@/shared/adTokens/AdTokenInput';
import { useAdTokenFindings } from '../adTokenFindings';

/** The layer's ad token marker, tinted by the worst token on it (MPN-1933). */
const AD_TOKEN_MARKER: Record<AdTokenKind, { color: string; title: string }> = {
  unknown: { color: '#dc2626', title: 'Has an unknown ad token' },
  instruction: { color: '#b45309', title: 'Has an ad token to fill in' },
  alias: { color: '#475569', title: 'Has ad tokens' },
  known: { color: '#475569', title: 'Has ad tokens' },
};

/**
 * A token in the layer's name (MPN-2068): not a finding, so it has its own marker, set apart
 * by glyph as well as by the shell's blue.
 */
const AD_TOKEN_NAME_MARKER = { color: '#2563eb', title: 'Ad token in layer name' };

interface LayerHeaderProps {
  layer: BaseLayerData;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  effectCount: number;
  dragHandleProps?: Record<string, unknown>;
}

export function LayerHeader({
  layer,
  isSelected,
  isExpanded,
  onToggleExpand,
  effectCount,
  dragHandleProps,
}: LayerHeaderProps) {
  const editor = useEditor();
  const { enabled: adTokensOn, byLayer, tokens } = useAdTokenFindings();
  const adTokens = byLayer[layer.id];
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(layer.name);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const toggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    editor.dispatchCommand(UPDATE_PROPERTY, {
      layerId: layer.id,
      property: 'visible',
      value: !layer.visible,
    });
  };

  const selectLayer = (e: React.MouseEvent) => {
    editor.dispatchCommand(SELECT_LAYER, { layerId: layer.id, addToSelection: e.metaKey || e.ctrlKey });
  };

  const commitName = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== layer.name) {
      editor.dispatchCommand(UPDATE_PROPERTY, {
        layerId: layer.id,
        property: 'name',
        value: trimmed,
      });
    }
    setEditing(false);
  };

  // The rename field's own keys, focus and look -- the same with or without the picker.
  const renameField = {
    onBlur: commitName,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') commitName();
      if (e.key === 'Escape') { setEditValue(layer.name); setEditing(false); }
    },
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    style: {
      flex: 1,
      fontSize: 12,
      fontWeight: 500,
      color: '#1e293b',
      background: '#fff',
      border: '1px solid #2563eb',
      borderRadius: 3,
      padding: '0 4px',
      height: 20,
      outline: 'none',
    } as React.CSSProperties,
  };

  return (
    <div
      onClick={(e) => selectLayer(e)}
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 28,
        paddingLeft: 4,
        paddingRight: 8,
        gap: 4,
        background: isSelected ? '#eff6ff' : 'transparent',
        borderBottom: '1px solid #e2e8f0',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {/* Drag handle */}
      <span
        {...(dragHandleProps ?? {})}
        style={{
          cursor: 'grab',
          color: '#cbd5e1',
          fontSize: 10,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          padding: '0 2px',
        }}
      >
        <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor">
          <circle cx="2" cy="2" r="1.2" /><circle cx="6" cy="2" r="1.2" />
          <circle cx="2" cy="7" r="1.2" /><circle cx="6" cy="7" r="1.2" />
          <circle cx="2" cy="12" r="1.2" /><circle cx="6" cy="12" r="1.2" />
        </svg>
      </span>

      {/* Expand toggle */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
        style={{
          width: 18,
          height: 18,
          background: 'none',
          border: 'none',
          cursor: effectCount > 0 ? 'pointer' : 'default',
          color: effectCount > 0 ? '#475569' : '#cbd5e1',
          fontSize: 10,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        disabled={effectCount === 0}
      >
        {effectCount > 0 ? (isExpanded ? '\u25BC' : '\u25B6') : '\u25B6'}
      </button>

      {/* Visibility checkbox */}
      <input
        type="checkbox"
        checked={layer.visible}
        onChange={() => {}}
        onClick={toggleVisibility}
        style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#2563eb' }}
      />

      {/* Layer name — double-click to edit */}
      {/* The ad token picker on `[` (MPN-1933); a name is not checked or highlighted. Plain
          with the Studio's ad tokens flagged off (MPN-2014). */}
      {editing && adTokensOn ? (
        <AdTokenInput
          inputRef={inputRef}
          highlight={false}
          tokens={tokens}
          value={editValue}
          onValueChange={setEditValue}
          {...renameField}
        />
      ) : editing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          {...renameField}
        />
      ) : (
        <span
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditValue(layer.name);
            setEditing(true);
          }}
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: isSelected ? 600 : 400,
            color: layer.visible ? '#1e293b' : '#94a3b8',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {layer.name}
        </span>
      )}

      {adTokens?.worst && (
        <span
          data-testid="ad-token-marker"
          data-kind={adTokens.worst}
          title={AD_TOKEN_MARKER[adTokens.worst].title}
          style={{ fontSize: 9, fontFamily: 'monospace', fontWeight: 700, color: AD_TOKEN_MARKER[adTokens.worst].color }}
        >
          [ ]
        </span>
      )}
      {adTokens?.inName && (
        <span
          data-testid="ad-token-name-marker"
          title={AD_TOKEN_NAME_MARKER.title}
          style={{ fontSize: 9, fontFamily: 'monospace', fontWeight: 700, color: AD_TOKEN_NAME_MARKER.color }}
        >
          [N]
        </span>
      )}

      {/* Type badge */}
      <span
        style={{
          fontSize: 9,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {layer.type}
      </span>
    </div>
  );
}
